import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { ON_FORM_CHANGE, SET_API_DATA, SET_ERROR } from "../../redux/constants";
import { checkValid } from "../../shared/validation";
import { sendData } from "../../utils/javascript";
import {
  clearFormData,
  setErrors,
  toggleModelShow,
} from "../../shared/CustomFunc";
import { ApiContainer } from "../../utils/api";
import { department_admin } from "../../utils/constant";
import { ERROR_MESSAGE } from "../../description/errorMessage.description";

const formPath = { parent: "eventScheduleInfo" };
const editDataSend = [
  "event_name",
  "event_timing_start_date",
  "event_timing_end_date",
  "lab_draws_start_date",
  "lab_draws_end_date",
  "lab_draws_start_time",
  "lab_draws_end_time",
  "lab_draw_type",
  "diagnostic_testing_start_date",
  "diagnostic_testing_end_date",
  "diagnostic_testing_address_line_1",
  "diagnostic_testing_address_line_2",
  "diagnostic_testing_city",
  "diagnostic_testing_zip_code",
  "provider_exam_start_date",
  "provider_exam_end_date",
  "csv_file",
  "lab_draw_address_line_1",
  "lab_draw_address_line_2",
  "lab_draw_zip_code",
  "lab_draw_city",
];

const NewCreateEventScheduleContainer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { api } = ApiContainer();
  const { eventId, organizationId, locationId, departmentId } = useParams();
  const { form } = useSelector((state) => state);
  const role = useSelector((state) => state?.app?.userData?.user_profile?.role);
  const eventScheduleInfo = useSelector(
    (state) => state.form.formData?.[formPath.parent]
  );
  const eventScheduleToEdit = useSelector(
    (state) => state.api?.eventScheduleToEdit
  );
  const viewRosterList = useSelector((state) => state.api.viewRosterList);
  const createEventScheduleModel = useSelector(
    (state) => state?.modelStates?.createEventSchedule
  );
  const formDataError = useSelector(
    (state) => state.form.formErrors?.[formPath.parent]
  );
  const formData = useSelector(
    (state) => state.form.formData?.[formPath.parent]
  );
  const modelState = useSelector((state) => state?.modelStates);

  const loaders = useSelector((state) => {
    return {
      createEventSchedule: state.api?.loader?.createEventSchedule,
      downloadCSVFile: state.api?.loader?.downloadCSVFile,
      viewRosterList: state.api?.loader?.viewRosterList,
    };
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [highlighted, setHighlighted] = useState(false);

  const eventEndPoints = `organization/${organizationId}/locations/${locationId}/departments/${departmentId}/event-schedule`;

  const isReadOnly = (fieldDateKey) =>
    role === department_admin ||
    dayjs(eventScheduleInfo?.[fieldDateKey]).isBefore(dayjs(), "day");

  const createEventScheduleAttribute = [
    {
      label: "Event Summary",
      subTitle: "Enter, review and confirm key event information",
      md: 6,
      value: [
        {
          control: "input",
          label: "Event Name",
          type: "text",
          name: "event_name",
          value: "",
          isRequired: true,
          validateAs: "name",
          placeHolder: "Enter Event Name",
          errorMessage: "Event name  is required.",
          fullWidth: true,
          readOnly: role === department_admin,
          md: 4,
        },
        {
          control: "date",
          label: "Event Start Date",
          name: "event_timing_start_date",
          value: null,
          isRequired: true,
          validateAs: "date",
          errorMessage: ERROR_MESSAGE?.start_date,
          fullWidth: true,
          md: 4,
          disablePast: true,
          readOnly: isReadOnly("event_timing_start_date"),
        },
        {
          control: "date",
          label: "Event End Date",
          name: "event_timing_end_date",
          value: "",
          isRequired: true,
          validateAs: "date",
          errorMessage: ERROR_MESSAGE?.end_date,
          fullWidth: true,
          md: 4,
          minDate:
            dayjs(eventScheduleInfo?.event_timing_start_date) ||
            dayjs().startOf("day"),
          disablePast: true,
          readOnly: isReadOnly("event_timing_end_date"),
        },
        {
          control: "input",
          type: "file",
          name: "csv_file",
          value: "",
          placeholder: "Upload your CSV files",
          label: "Event Roster",
          fullWidth: true,
          isRequired: !(eventScheduleToEdit && eventScheduleToEdit.id),
          errorMessage: ERROR_MESSAGE?.end_date,
          question: "Invite Members",
          readOnly: isReadOnly("event_timing_end_date"),
        },
      ],
    },
    {
      label: "Lab Draws",
      subTitle: "Assign the lab draw type and select draw dates.",
      md: 6,
      value: [
        {
          control: "select",
          label: "Lab Draw Type",
          name: "lab_draw_type",
          value: 0,
          validateAs: "selectMenu",
          isRequired: true,
          fullWidth: true,
          placeHolder: "Select lab draw type",
          errorMessage: ERROR_MESSAGE?.lab_draw_type,
          options: [
            { key: "On-site", value: "on_site" },
            {
              key: "PSC - Labcorp",
              value: "psc_labcorp",
            },
            {
              key: "PSC - CPL",
              value: "psc_cpl",
            },
          ],
          md: 4,
          variant: "standard",
          readOnly: isReadOnly("event_timing_end_date"),
        },
        {
          control: "time",
          label: "Start Time",
          name: "lab_draws_start_time",
          value: null,
          isRequired: true,
          validateAs: "time",
          errorMessage: "Start time is required",
          fullWidth: true,
          md: 4,
          readOnly: isReadOnly("event_timing_end_date"),
        },
        {
          control: "time",
          label: "End Time",
          name: "lab_draws_end_time",
          value: null,
          isRequired: true,
          validateAs: "time",
          errorMessage: "End time is required",
          fullWidth: true,
          md: 4,
          readOnly: isReadOnly("event_timing_end_date"),
        },
        {
          control: "date",
          label: "Start Date",
          name: "lab_draws_start_date",
          value: null,
          isRequired: true,
          validateAs: "date",
          errorMessage: ERROR_MESSAGE?.start_date,
          fullWidth: true,
          md: 6,
          disablePast: true,
          minDate:
            dayjs(eventScheduleInfo?.event_timing_start_date) ||
            dayjs().startOf("day"),
          maxDate: dayjs(eventScheduleInfo?.event_timing_end_date),
          readOnly: isReadOnly("event_timing_end_date"),
        },
        {
          control: "date",
          label: "End Date",
          name: "lab_draws_end_date",
          value: "",
          isRequired: true,
          validateAs: "date",
          errorMessage: ERROR_MESSAGE?.end_date,
          fullWidth: true,
          md: 6,
          disablePast: true,
          minDate:
            dayjs(eventScheduleInfo?.lab_draws_start_date) ||
            dayjs(eventScheduleInfo?.event_timing_start_date) ||
            dayjs().startOf("day"),
          maxDate: dayjs(eventScheduleInfo?.event_timing_end_date),
          readOnly: isReadOnly("event_timing_end_date"),
        },
        ...(eventScheduleInfo?.lab_draw_type === "on_site"
          ? [
              {
                control: "input",
                label: "Address Line 1",
                type: "text",
                name: "lab_draw_address_line_1",
                value: "",
                placeHolder: "Enter address line 1",
                errorMessage: ERROR_MESSAGE?.address_line_1,
                fullWidth: true,
                readOnly: isReadOnly("event_timing_end_date"),
                md: 6,
              },
              {
                control: "input",
                label: "Address Line 2",
                type: "text",
                name: "lab_draw_address_line_2",
                value: "",
                placeHolder: "Enter address line 2",
                errorMessage: ERROR_MESSAGE?.address_line_2,
                fullWidth: true,
                md: 6,
                readOnly: isReadOnly("event_timing_end_date"),
              },
              {
                control: "input",
                label: "City",
                type: "text",
                name: "lab_draw_city",
                value: "",
                placeHolder: "Enter city",
                errorMessage: ERROR_MESSAGE?.city,
                fullWidth: true,
                md: 6,
                readOnly: isReadOnly("event_timing_end_date"),
              },
              {
                control: "input",
                label: "Zip Code",
                type: "number",
                name: "lab_draw_zip_code",
                value: "",
                validateAs: "zipCodeOptional",
                placeHolder: "Enter zip code",
                errorMessage: ERROR_MESSAGE?.zip_code,
                fullWidth: true,
                md: 6,
                sx: {
                  '& input[type="number"]::-webkit-inner-spin-button, input[type="number"]::-webkit-outer-spin-button':
                    {
                      WebkitAppearance: "none",
                    },
                },
                readOnly: isReadOnly("event_timing_end_date"),
              },
            ]
          : []),
      ],
    },
    {
      label: "ANNUAL PHYSICALS",
      subTitle:
        "Schedule the annual physical assessments like X-rays and CPETs",
      md: 6,
      value: [
        {
          control: "date",
          label: "Start Date",
          name: "diagnostic_testing_start_date",
          value: null,
          isRequired: true,
          validateAs: "date",
          errorMessage: ERROR_MESSAGE?.start_date,
          fullWidth: true,
          md: 6,
          disablePast: true,
          minDate:
            dayjs(eventScheduleInfo?.event_timing_start_date) ||
            dayjs().startOf("day"),
          maxDate: dayjs(eventScheduleInfo?.event_timing_end_date),
          readOnly: isReadOnly("event_timing_end_date"),
        },
        {
          control: "date",
          label: "End Date",
          name: "diagnostic_testing_end_date",
          value: "",
          isRequired: true,
          validateAs: "date",
          errorMessage: ERROR_MESSAGE?.end_date,
          fullWidth: true,
          md: 6,
          disablePast: true,
          minDate:
            dayjs(eventScheduleInfo?.diagnostic_testing_start_date) ||
            dayjs(eventScheduleInfo?.event_timing_start_date) ||
            dayjs().startOf("day"),
          maxDate: dayjs(eventScheduleInfo?.event_timing_end_date),
          readOnly: isReadOnly("event_timing_end_date"),
        },
        {
          control: "input",
          label: "Address Line 1",
          type: "text",
          name: "diagnostic_testing_address_line_1",
          value: "",
          isRequired: true,
          validateAs: "name",
          placeHolder: "Enter address line 1",
          errorMessage: ERROR_MESSAGE?.address_line_1,
          fullWidth: true,
          readOnly: isReadOnly("event_timing_end_date"),
          md: 6,
        },
        {
          control: "input",
          label: "Address Line 2",
          type: "text",
          name: "diagnostic_testing_address_line_2",
          value: "",
          placeHolder: "Enter address line 2",
          errorMessage: ERROR_MESSAGE?.address_line_2,
          fullWidth: true,
          md: 6,
          readOnly: isReadOnly("event_timing_end_date"),
        },
        {
          control: "input",
          label: "City",
          type: "text",
          name: "diagnostic_testing_city",
          value: "",
          isRequired: true,
          validateAs: "name",
          placeHolder: "Enter city",
          errorMessage: ERROR_MESSAGE?.city,
          fullWidth: true,
          md: 6,
          readOnly: isReadOnly("event_timing_end_date"),
        },
        {
          control: "input",
          label: "Zip Code",
          type: "number",
          name: "diagnostic_testing_zip_code",
          value: "",
          isRequired: true,
          validateAs: "zipCode",
          placeHolder: "Enter zip code",
          errorMessage: ERROR_MESSAGE?.zip_code,
          fullWidth: true,
          md: 6,
          sx: {
            '& input[type="number"]::-webkit-inner-spin-button, input[type="number"]::-webkit-outer-spin-button':
              {
                WebkitAppearance: "none",
              },
          },
          readOnly: isReadOnly("event_timing_end_date"),
        },
      ],
    },
    {
      label: "Provider Exam",
      subTitle: "Set up provider exam dates and slots.",
      md: 6,
      value: [
        {
          control: "date",
          label: "Start Date",
          name: "provider_exam_start_date",
          value: null,
          isRequired: true,
          validateAs: "date",
          errorMessage: ERROR_MESSAGE?.start_date,
          fullWidth: true,
          md: 6,
          disablePast: true,
          minDate:
            dayjs(eventScheduleInfo?.event_timing_start_date) ||
            dayjs().startOf("day"),
          maxDate: dayjs(eventScheduleInfo?.event_timing_end_date),
          readOnly: isReadOnly("event_timing_end_date"),
        },
        {
          control: "date",
          label: "End Date",
          name: "provider_exam_end_date",
          value: "",
          isRequired: true,
          validateAs: "date",
          disablePast: true,
          errorMessage: ERROR_MESSAGE?.end_date,
          fullWidth: true,
          md: 6,
          minDate:
            dayjs(eventScheduleInfo?.provider_exam_start_date) ||
            dayjs(eventScheduleInfo?.event_timing_start_date) ||
            dayjs().startOf("day"),
          maxDate: dayjs(eventScheduleInfo?.event_timing_end_date),
          readOnly: isReadOnly("event_timing_end_date"),
        },
      ],
    },
  ];

  const apiCall = async () => {
    if (eventId) {
      const formData = new FormData();

      const dataToSend = sendData(editDataSend, eventScheduleInfo);
      dataToSend.send_emails = Boolean(eventScheduleInfo?.send_emails);

      Object.entries(dataToSend || {})?.forEach(([key, value]) => {
        if (key === "csv_file" && value instanceof File) {
          formData.append(key, value);
        } else if (key === "csv_file" && value?.file instanceof File) {
          formData.append(key, value?.file);
        } else if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });
      const data = await api({
        method: "PUT",
        endPoint: `${eventEndPoints}/${eventId}`,
        data: formData,
        showToastMessage: true,
        urlencoded: false,
        needLoader: true,
        parent: "createEventSchedule",
        header: { "Content-Type": "multipart/form-data" },
      });
    } else {
      const formData = new FormData();
      Object.entries(eventScheduleInfo || {}).forEach(([key, value]) => {
        if (key === "csv_file" && value instanceof File) {
          formData.append(key, value);
        } else if (key === "csv_file" && value?.file instanceof File) {
          formData.append(key, value.file);
        } else if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });
      const data = await api({
        method: "POST",
        endPoint: `${eventEndPoints}`,
        data: formData,
        showToastMessage: true,
        parent: "createEventSchedule",
        urlencoded: false,
        needLoader: true,
        header: { "Content-Type": "multipart/form-data" },
      });
      if (data?.status) {
        navigate(
          `/org-admin/${organizationId}/locations/${locationId}/departments/${departmentId}/event/${data?.data?.event_schedule_id}`
        );
      }
    }
    dispatch(toggleModelShow("createEventSchedule", true));
  };

  const setFormData = (name, value) => {
    dispatch(setErrors({ errors: formDataError, value, name, formPath }));
    dispatch({
      type: ON_FORM_CHANGE,
      payload: {
        formData: {
          ...form.formData,
          [formPath.parent]: { ...formData, [name]: value },
        },
      },
    });
  };

  const handleCreateEventSchedule = (e) => {
    e.preventDefault();
    let errors = {};

    createEventScheduleAttribute?.forEach((item) => {
      Object.values(item.value)?.forEach((control) => {
        const { name, validateAs, label } = control;
        errors = {
          ...errors,
          [name]: validateAs
            ? checkValid({
                validateAs,
                name,
                value: eventScheduleInfo?.[name],
                label,
              })
            : false,
        };
      });
    });
    const isEdit = eventScheduleToEdit && eventScheduleToEdit?.id;
    if (!isEdit) {
      const csvFile = eventScheduleInfo?.csv_file;
      const isCSVFileValid =
        csvFile instanceof File ||
        (csvFile?.file && csvFile.file instanceof File);

      if (!isCSVFileValid) {
        errors = {
          ...errors,
          csv_file: "Please upload Event Roster CSV file",
        };
      }
    }

    dispatch({
      type: SET_ERROR,
      payload: {
        formErrors: {
          ...form.formErrors,
          [formPath.parent]: { ...errors },
        },
      },
    });

    const hasErrors = Object.values(errors).some((err) => err);
    if (!hasErrors) {
      if (isEdit) {
        setShowDialog(!showDialog);
      } else {
        apiCall();
      }
    }
  };

  const handleViewEventSchedule = async (eventId) => {
    if (eventId) {
      const data = await api({
        method: "GET",
        endPoint: `${eventEndPoints}/${eventId}`,
        showToastMessage: false,
        urlencoded: false,
        needLoader: true,
        parent: "createEventSchedule",
        needFullResponse: false,
      });
      if (data?.status) {
        dispatch({
          type: SET_API_DATA,
          payload: {
            eventScheduleToEdit: data?.data,
          },
        });
        dispatch({
          type: ON_FORM_CHANGE,
          payload: {
            formData: {
              ...form.formData,
              eventScheduleInfo: data?.data,
            },
          },
        });
      }
    }
  };
  const handleCancleEditEvent = () => {
    setIsEditMode((prev) => !prev);
    dispatch({
      type: ON_FORM_CHANGE,
      payload: {
        formData: {
          ...form.formData,
          eventScheduleInfo: eventScheduleToEdit,
        },
      },
    });
  };

  useEffect(() => {
    if (!eventId) {
      dispatch({
        type: SET_API_DATA,
        payload: {
          eventScheduleToEdit: null,
          eventScheduleInfo: null,
        },
      });
      dispatch(clearFormData(formPath.parent));
    }
  }, [eventId]);

  useEffect(() => {
    if (!eventId) return;
    if (eventScheduleInfo?.lab_draw_type !== "on_site") {
      const {
        lab_draw_address_line_1,
        lab_draw_address_line_2,
        lab_draw_zip_code,
        lab_draw_city,
        ...restInfo
      } = eventScheduleInfo || {};

      dispatch({
        type: ON_FORM_CHANGE,
        payload: {
          formData: {
            ...form.formData,
            [formPath.parent]: restInfo,
          },
        },
      });
    }
  }, [eventScheduleInfo?.lab_draw_type]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files?.length > 0) {
      const file = files[0];
      const isCSV = file.type === "text/csv" || file.name.endsWith(".csv");
      if (!isCSV) {
        dispatch({
          type: SET_ERROR,
          payload: {
            formErrors: {
              ...form.formErrors,
              [formPath.parent]: {
                ...formDataError,
                [name]: "Only CSV files are allowed",
              },
            },
          },
        });
        setFormData(name, null);
        return;
      }
      dispatch({
        type: SET_ERROR,
        payload: {
          formErrors: {
            ...form.formErrors,
            [formPath.parent]: {
              ...formDataError,
              [name]: "",
            },
          },
        },
      });

      setFormData(name, file);
    } else {
      setFormData(name, value);
    }
  };
  const handleMobileChange = (mobile, name) => {
    setFormData(name, `+${mobile}`);
  };
  const handleTimeChange = (newValue, name) => {
    if (!newValue) return;
    setFormData(name, newValue);
  };
  const handleDateChange = (value, name) => {
    setFormData(name, new Date(value).toLocaleDateString("en-US"));
  };
  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData(name, checked);
  };
  const handleChangePhoto = (e) => {
    const { name, files } = e.target;
    setFormData(
      name,
      files && files?.[0]
        ? {
            photo_url: URL.createObjectURL(files[0]),
            photo_file: files[0],
          }
        : null
    );
    if (highlighted) setHighlighted(false);
  };
  const DownloadUploadedCSVFile = async () => {
    const data = await api({
      method: "GET",
      endPoint: `${eventEndPoints}/${eventId}/event-roster/download`,
      showToastMessage: false,
      urlencoded: false,
      needLoader: true,
      parent: "downloadCSVFile",
      needFullResponse: false,
    });

    if (data?.status && data?.data) {
      const csvContent = data?.data;
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

      const fileName = `Event_Schedule_${eventScheduleToEdit?.event_id}.csv`;
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };
  const handleViewRoster = async () => {
    const data = await api({
      method: "GET",
      endPoint: `${eventEndPoints}/${eventId}/event-roster`,
      showToastMessage: false,
      urlencoded: false,
      needLoader: true,
      parent: "viewRosterList",
      needFullResponse: false,
    });

    if (data?.status) {
      dispatch(toggleModelShow("viewRosterModal"));
      dispatch({
        type: SET_API_DATA,
        payload: {
          viewRosterList: data?.data,
        },
      });
    }
  };
  const removeUploadedFile = () => {
    dispatch({
      type: ON_FORM_CHANGE,
      payload: {
        formData: {
          ...form.formData,
          [formPath.parent]: {
            ...formData,
            csv_file: null,
            csv_file_data: null,
          },
        },
        formErrors: {
          ...form.formErrors,
          [formPath.parent]: {
            ["csv_file"]: "",
          },
        },
      },
    });
  };
  const downloadTemplateFile = async () => {
    const fileUrl =
      "https://vasana-static.s3.us-east-1.amazonaws.com/set-event-schedule.csv";
    const fileName = "Create Event Schedule.csv";
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    link.click();
  };

  const onEditCreateClick = (row) => {
    apiCall();
    setShowDialog(!showDialog);
    setIsEditMode((prev) => !prev);
  };

  const onHandleCancle = () => {
    setShowDialog(!showDialog);
    setIsEditMode((prev) => !prev);
  };

  const onDragEnter = (e) => {
    e.preventDefault();
    setHighlighted(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    setHighlighted(false);
  };

  useEffect(() => {
    handleViewEventSchedule(eventId);
  }, [eventId]);

  return {
    eventId,
    createEventScheduleAttribute,
    handleCreateEventSchedule,
    handleChange,
    formPath,
    dispatch,
    modelState,
    handleMobileChange,
    handleDateChange,
    handleTimeChange,
    handleSwitchChange,
    handleChangePhoto,
    DownloadUploadedCSVFile,
    handleViewRoster,
    removeUploadedFile,
    downloadTemplateFile,
    viewRosterList,
    loaders,
    showDialog,
    onEditCreateClick,
    onHandleCancle,
    isEditMode,
    setIsEditMode,
    handleCancleEditEvent,
    highlighted,
    onDragEnter,
    onDragLeave,
    eventScheduleInfo,
  };
};

export default NewCreateEventScheduleContainer;
